import React from "react";
import useSiteContent from "@/lib/useSiteContent";
import { maxAnnualSavingPercent, payFeeRate, portingFacts, teamSeatFacts } from "./pricingFacts";

/**
 * WHICH LOCALE STRING EACH ANSWER USES, and what gets interpolated into it.
 *
 * BUG-205: four of the five answers state pricing facts — the two data-porting prices, the
 * included seat count and the per-seat overage, the rate on client payments, the annual
 * saving. All four were typed into the dictionary, three times each, once per locale, and
 * so drifted from the catalogue rendered higher up the same page the moment anyone edited
 * a plan or a SKU. They are DERIVED now (`./pricingFacts`) and interpolated; the strings
 * carry `{placeholders}` and not a single digit.
 *
 * EVERY ANSWER HAS TWO VARIANTS, and the second is not a fallback for a broken state — it
 * is the answer for a page whose catalogue has not arrived yet. `PlatformLanding` mounts
 * with three empty arrays and fills them from four awaited GETs, so the number-free
 * variant is what the FIRST PAINT renders, on every visit. It also covers a catalogue the
 * facts genuinely cannot be read from (a deactivated SKU, an unlimited-seat plan), where
 * saying less is the only truthful option. Both variants are editable site copy.
 */
function faqItems(text, { plans, skus, platformFees, currency }) {
  const porting = portingFacts(skus, currency);
  const seats = teamSeatFacts(plans, currency);
  const rate = payFeeRate(platformFees);
  const percent = maxAnnualSavingPercent(plans, currency);
  return [
    { n: "1", question: text("faq.q1"), answer: text("faq.a1") },
    { n: "2", question: text("faq.q2"), answer: porting ? text("faq.a2", porting) : text("faq.a2Generic") },
    { n: "3", question: text("faq.q3"), answer: seats ? text("faq.a3", seats) : text("faq.a3Generic") },
    { n: "4", question: text("faq.q4"), answer: rate ? text("faq.a4", { rate }) : text("faq.a4Generic") },
    {
      n: "5",
      question: text("faq.q5"),
      answer: percent !== null ? text("faq.a5", { percent }) : text("faq.a5Generic"),
    },
  ];
}

/**
 * `id="about"` IS A CONTRACT, not decoration: it is one of the registry's ANCHORS
 * (backend/modules/site_content/registry.py), and the seeded header "About" link targets
 * it. Renaming it turns every `#about` link into a no-op anchor — the BUG-204 failure
 * mode — which is why the backend drift test asserts every anchor is rendered as an id.
 */
export default function Faq({ plans = [], skus = [], platformFees = [], currency }) {
  const { text } = useSiteContent();
  const items = faqItems(text, { plans, skus, platformFees, currency });
  return (
    <section id="about" className="py-28 bg-sol-ivory border-t border-sol-midnight/5">
      <div className="max-w-3xl mx-auto px-6 lg:px-10">
        <h2 className="font-display text-4xl text-sol-midnight mb-12 text-center">{text("faq.title")}</h2>
        <div className="space-y-3">
          {items.map(({ n, question, answer }) => (
            <details
              key={`faq-${n}`}
              className="bg-white border border-sol-midnight/8 rounded-2xl px-6 py-5 group"
              data-testid={`faq-item-${n}`}
            >
              <summary className="cursor-pointer flex items-center justify-between font-medium text-sol-midnight list-none">
                <span>{question}</span>
                <span className="text-sol-gold text-2xl group-open:rotate-45 transition-transform">+</span>
              </summary>
              <p className="mt-4 text-sol-midnight/60 leading-relaxed">
                {answer}
              </p>
            </details>
          ))}
        </div>
      </div>
    </section>
  );
}
